// RESPONSE TIMES — how fast clients hear back, as the mock draws it.
//
// ⭐ PURE, AND THE PAGE PRINTS WHAT IT RETURNS. Waits come in already decided
// (lib/client-slack-waits.ts) and already tied to a brand
// (lib/server/client-slack-response-times.ts); this turns them into every
// number and every sentence on /brands/response-times. Nothing here reads.
//
// THE CLOCK IS YWS'S WORKING HOURS (it replaced the wall clock): how long a
// wait took counts only the hours an admin set (default Mon–Fri 9am–5pm New
// York; lib/working-hours.ts `workingSeconds`, the one clock), so a message at
// 4:30pm Friday answered at 9:30am Monday took 1 hour, and "a day" is a working
// day. Which day and hour a client WROTE (the charts' columns) stays New York's
// calendar, as the mock's. Every time here is Slack's, in SECONDS.
//
// ⚠️ A WAIT STILL OPEN AND YOUNGER THAN THE LINE IS NOT A MISS. The mock counted
// a message written ten minutes ago as "not answered within the hour", so the
// headline fell every time a client wrote and rose an hour later however fast
// somebody replied. Here each line (1 hour, 4 hours) is asked only of the waits
// it can already answer: answered, or open for longer than the line. The rest
// are named beside the number ("2 more still inside the hour"), never folded
// into it. Found before the page was built: the mock's own `within`.
//
// ⭐ THE MODEL DECIDES "NEEDS A REPLY" for every wait it has read as it stands;
// the rules decide the rest (a wait it hasn't read yet, one whose words changed
// since, and every new one once the day's ceiling is spent). The footer says
// which, in a line each: what the model set aside, and how many of the counted
// messages the rules decided, with why when it is the ceiling.

import type { FooterNote } from "@/components/dashboards/dashboard-footer";
import type { SlackPageWord } from "@/lib/client-slack-channels";
import { DASH } from "@/lib/dashboard-window";
import { addDaysUtc, formatShort } from "@/lib/schedule-dates";
import { isAllHours, workingDayLength, workingHoursText, workingSeconds, type WorkingHours } from "@/lib/working-hours";

export const RESPONSE_TIMES_PATH = "/brands/response-times";

/** The two windows, in days: the mock's Last 7 · Last 30. */
export type RtWin = 7 | 30;
export const RT_WINDOWS: readonly RtWin[] = [7, 30];
export const DEFAULT_RT_WIN: RtWin = 7;
export const parseRtWin = (v: string | undefined): RtWin => (v === "30" ? 30 : 7);

export const HOUR = 3600;
export const DAY = 86_400;
/** The headline's line, and the goal's. */
const FAST = HOUR;
const GOAL = 4 * HOUR;
/** An hour of the day with fewer client messages than this is drawn grey: its percentage is shaky. */
const SHAKY = 5;

const TZ = "America/New_York";
const dayFmt = new Intl.DateTimeFormat("en-CA", { timeZone: TZ });
const hourFmt = new Intl.DateTimeFormat("en-US", { timeZone: TZ, hour: "numeric", hourCycle: "h23" });
/** A Slack time's day in New York, `YYYY-MM-DD`. */
export const nyDay = (t: number): string => dayFmt.format(new Date(t * 1000));
/** A Slack time's hour in New York, 0–23. */
export const nyHour = (t: number): number => Number(hourFmt.format(new Date(t * 1000))) % 24;

/**
 * A duration as the mock writes it: `7m`, `5h`, `3d`. `day` is what a `d`
 * stands for: 24 hours on the wall clock, a working day (8 hours on 9–5) for
 * a wait's length; hours are written up to two of them. Under a minute is
 * written as one; nothing at all as `0m`.
 */
export function dur(s: number, day: number = DAY): string {
  // No working time at all (a weekend message, answered or not by Monday 9am) is
  // none, not the minute a fast wall-clock reply rounds up to.
  if (s <= 0) return "0m";
  if (s < HOUR) return `${Math.max(1, Math.round(s / 60))}m`;
  if (s < 2 * day) return `${Math.round(s / HOUR)}h`;
  return `${Math.round(s / day)}d`;
}

/** How long a wait has waited, on the working clock: to its first reply, or to `now` while open. */
export const waited = (w: { start: number; end: number | null }, now: number, hours: WorkingHours): number => workingSeconds(w.start, w.end ?? now, hours);

const pct = (a: number, b: number): number => Math.round((100 * a) / b);
const pctText = (a: number, b: number): string => (b === 0 ? DASH : `${pct(a, b)}%`);
const plural = (n: number, one: string, many = `${one}s`): string => `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
const listOf = (names: string[]): string =>
  names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

// ─── The inputs ────────────────────────────────────────────────────────────

/** A wait that needs a reply, on one brand. Waits that need none never reach this file. */
export interface RtWait {
  brand: string;
  start: number;
  end: number | null;
  /**
   * Who said it needs a reply: the model's read of it, the rules because the
   * model hasn't read it as it stands, or a person who pulled it back from
   * "no reply needed" in the Client Inbox.
   */
  by: "model" | "rules" | "person";
  /**
   * Someone archived it in the Client Inbox without a reply: still no
   * reply, so every line counts it as missed and "No reply yet" as one, but
   * nobody is waiting on it any more, so "Waiting now" and "still waiting"
   * leave it out.
   */
  archived?: boolean;
}

/**
 * A wait that needs no reply: the model read it so (a thanks, an FYI), or a
 * person marked it so in the Client Inbox. Out of every number, counted in the
 * footer.
 */
export interface RtSetAside {
  brand: string;
  start: number;
  by: "model" | "person";
}

/**
 * A wait someone marked answered elsewhere in the Client Inbox: it was answered,
 * in a DM or on a call, at a time nobody recorded. Out of every number (it
 * can't be timed) and out of "Waiting now", counted in the footer.
 */
export interface RtElsewhere {
  brand: string;
  start: number;
  /**
   * `reaction`: a quick acknowledgement someone at YWS reacted to
   * (lib/client-slack-inbox.ts `decideWait`): out of the numbers for the same
   * reason, since Slack doesn't say when a reaction came. `settled`: the
   * answered skill read it as settled with no answer in Slack (moved to a call
   * or a DM, replaced by the client's later message, or answered by the client
   * themselves): nothing owed any more, and nothing to time. Absent: marked.
   */
  how?: "elsewhere" | "reaction" | "settled";
}

/** The triage's day against its own daily limit (skills/skills.ts), as the footer needs it. */
export interface RtTriage {
  /** The limit is spent, or off: the model reads nothing more today. */
  exhausted: boolean;
  /** The limit in dollars, 0 when off. */
  limitUsd: number;
}

/**
 * WHEN AN ARCHIVED BRAND'S SLACK STOPPED. Nothing reads an archived brand's
 * channels, so its stored messages end where the last read left them. Judged
 * against today, a message open then would wait longer every day and the
 * window would slide to empty, so its numbers are judged at this moment
 * instead: the same rules, with this as its "now". In seconds.
 */
export interface RtAsOf {
  /** The moment it is judged at, and the date its line gives (lib/server/client-slack-response-times.ts `archivedMoments`). */
  at: number;
  /** Its newest archiving `brand.status` row, or null when it has none. */
  archivedAt: number | null;
}

/** A brand's "now": its moment when it is archived and has one, else the page's. */
export const brandNow = (b: { archived: boolean; asOf?: RtAsOf } | undefined, now: number): number => (b?.archived && b.asOf ? b.asOf.at : now);

const shortDay = (t: number, now: number): string => {
  const day = nyDay(t);
  return day.slice(0, 4) === nyDay(now).slice(0, 4) ? formatShort(day) : `${formatShort(day)}, ${day.slice(0, 4)}`;
};

/** The line an archived brand's numbers carry: "As of Oct 3", and why on hover. */
export function slackAsOfLine(a: RtAsOf, now: number): { text: string; title: string } {
  const read = shortDay(a.at, now);
  const why = "An archived brand's Slack channels aren't read, so these are its messages";
  const archived = a.archivedAt === null ? null : shortDay(a.archivedAt, now);
  const title =
    archived === null
      ? `${why} as of its last read, ${read}.`
      : archived === read
        ? `Archived ${archived}. ${why} as they stood then.`
        : `Archived ${archived}; last read ${read}. ${why} as of that read.`;
  return { text: `As of ${read}`, title };
}

/** One brand in the reader's reach, and where its Slack stands (lib/client-slack-channels.ts `slackPageState`). */
export interface RtBrand {
  slug: string;
  name: string;
  archived: boolean;
  /** An archived brand's moment; absent for an active brand, and for an archived one with no channel ever read. */
  asOf?: RtAsOf;
  /** The pod's address (`?pod=`): its lead's slug, `none` for no pod, null for a lead with no address. */
  pod: string | null;
  podLabel: string;
  word: SlackPageWord;
  /** The page body for this brand alone when nothing of it is read. */
  empty: { title: string; act: string } | null;
  /** `#name` of each channel of it the bot is still reading the 90 days of. */
  stillReading: string[];
  /** The YWS person who wrote most in its channels over the read: the mock's "Replies most". */
  repliesMost: string | null;
}

export type RtSort = "name" | "n" | "p1" | "p4" | "med" | "waiting" | "pod" | "lead";
const SORTS: readonly RtSort[] = ["name", "n", "p1", "p4", "med", "waiting", "pod", "lead"];
export const parseRtSort = (v: string | undefined): RtSort => (SORTS as readonly string[]).includes(v ?? "") ? (v as RtSort) : "p1";
/** Which way a column sorts on its first press: words A→Z, rates slowest first, counts most first. */
const FIRST_DIR: Record<RtSort, 1 | -1> = { name: 1, n: -1, p1: 1, p4: 1, med: -1, waiting: -1, pod: 1, lead: 1 };

export interface RtReading {
  /** `?pod=`: a pod's address, or null for All. */
  pod: string | null;
  /** `?brand=`: a brand's slug, or null for the whole pod. */
  brand: string | null;
  win: RtWin;
  sort: RtSort;
  dir: 1 | -1;
}

export const parseRtDir = (v: string | undefined, sort: RtSort): 1 | -1 =>
  v === "asc" ? 1 : v === "desc" ? -1 : FIRST_DIR[sort];

// ─── The model ─────────────────────────────────────────────────────────────

export interface RtPill {
  value: string;
  label: string;
  count: number;
  /** Nothing in the window: drawn quieter, as the mock's `.pill.zero`. */
  muted: boolean;
  href: string | null;
  on: boolean;
}

export interface RtColumn {
  key: string;
  label: string;
  /** Share answered within the hour, 0–1; null when no wait there can answer it yet. */
  v: number | null;
  /** Drawn grey: outside the window (by day), or too few to trust (by hour). */
  muted: boolean;
  /** The hover, in words. */
  title: string;
}

export interface RtRow {
  slug: string;
  name: string;
  /** An archived brand's "As of Oct 3", under its name: its row is judged then. Null for an active brand. */
  asOf: { text: string; title: string } | null;
  href: string;
  on: boolean;
  n: number;
  p1: number | null;
  p1Text: string;
  p4: number | null;
  p4Text: string;
  /** The typical reply, in seconds. */
  med: number | null;
  medText: string;
  waiting: number;
  waitingText: string;
  /** The Client Inbox, on this brand alone; null when nothing is waiting. */
  waitingHref: string | null;
  pod: string;
  lead: string;
}

export interface RtModel {
  pods: RtPill[];
  /** The brand tier: shown once a pod is picked, as the mock's. */
  brands: RtPill[] | null;
  windows: Array<{ key: string; label: string; href: string }>;
  /** A picked archived brand's "As of Oct 3": every number on the page is judged then. */
  asOf: { text: string; title: string } | null;
  /** The body's one empty state; null when there are numbers to draw. */
  empty: { title: string; act: string; settings: boolean } | null;
  headline: { value: string; sub: string; delta: { text: string; tone: "up" | "down" | "flat" } | null };
  /** `href`: where the tile goes (Waiting now opens the Client Inbox on this scope). */
  tiles: Array<{ key: string; label: string; value: string; hint: string; href: string | null }>;
  daily: RtColumn[];
  buckets: Array<{ bucket: string; value: number; share: number | null; bad: boolean }>;
  bucketTotal: number;
  hours: RtColumn[];
  columns: Array<{ key: RtSort; label: string; right: boolean; href: string; arrow: string }>;
  rows: RtRow[];
  /** What the table says when no brand in it had a client message in the window. */
  rowsEmpty: string | null;
  notes: FooterNote[];
}

/**
 * The scope bar of a client Slack page (Response Times, the Client Inbox):
 * every brand in reach a pill with its count, the pods summed, and the brand
 * tier once a pod is picked — the brands with something to read first. A lead
 * with no slug has no address (an email never goes in a URL): a label, its
 * brands under All. `go` makes a pill's link from the pod and brand it picks.
 */
export function slackScopePills(input: {
  brands: readonly RtBrand[];
  countOf: (slug: string) => number;
  pod: string | null;
  brand: string | null;
  go: (o: { pod?: string | null; brand?: string | null }) => string;
}): { pods: RtPill[]; brands: RtPill[] | null; podBrands: RtBrand[] } {
  const { brands, countOf, go } = input;
  const podOrder: Array<{ value: string | null; label: string }> = [];
  for (const b of brands) if (!podOrder.some((p) => p.value === b.pod && p.label === b.podLabel)) podOrder.push({ value: b.pod, label: b.podLabel });
  podOrder.sort((a, b) => (a.value === "none" ? 1 : b.value === "none" ? -1 : a.label.localeCompare(b.label)));
  // An archived brand's pill counts it as of its moment; a pod's, and All, leave it
  // out, as the numbers over a pod do (`responseTimes`, `clientInbox`).
  const sum = (list: readonly RtBrand[]): number => list.reduce((n, b) => n + (b.archived ? 0 : countOf(b.slug)), 0);
  const allCount = sum(brands);
  const pods: RtPill[] = [
    { value: "", label: "All", count: allCount, muted: allCount === 0, href: go({ pod: null, brand: null }), on: input.pod === null },
    ...podOrder.map((p) => {
      const n = sum(brands.filter((b) => b.pod === p.value && b.podLabel === p.label));
      return {
        value: p.value ?? `label:${p.label}`,
        label: p.label,
        count: n,
        muted: n === 0,
        href: p.value === null ? null : go({ pod: p.value, brand: null }),
        on: p.value !== null && input.pod === p.value,
      };
    }),
  ];
  const podBrands = input.pod === null ? brands.slice() : brands.filter((b) => b.pod === input.pod);
  const brandPills: RtPill[] | null =
    input.pod === null
      ? null
      : [
          { value: "", label: "All", count: sum(podBrands), muted: sum(podBrands) === 0, href: go({ brand: null }), on: input.brand === null },
          ...podBrands
            .slice()
            // The brands with something to read first, then by name: the mock's order.
            .sort((a, b) => Number(b.word === "reading") - Number(a.word === "reading") || a.name.localeCompare(b.name))
            .map((b) => {
              const n = countOf(b.slug);
              return {
                value: b.slug,
                label: b.archived ? `${b.name} (archived)` : b.name,
                count: n,
                muted: n === 0,
                href: go({ brand: b.slug }),
                on: input.brand === b.slug,
              };
            }),
        ];
  return { pods, brands: brandPills, podBrands };
}

/**
 * A line asked of a set of waits, in working time: how many it can answer yet,
 * and how many are inside it. An open wait is asked once it has waited longer
 * than the line in working hours, so a Friday-evening message is still
 * "inside the hour" all weekend.
 */
export function line(ws: readonly RtWait[], now: number, s: number, hours: WorkingHours): { ok: number; asked: number; pending: number } {
  let ok = 0;
  let asked = 0;
  for (const w of ws) {
    if (w.end !== null) {
      asked++;
      if (waited(w, now, hours) <= s) ok++;
    } else if (waited(w, now, hours) > s) asked++;
  }
  return { ok, asked, pending: ws.length - asked };
}

/** The mock's "typical first reply", in working time: the middle of the answered waits (the upper one of an even count). */
export function typical(ws: readonly RtWait[], hours: WorkingHours): number | null {
  const answered = ws.filter((w) => w.end !== null).map((w) => waited(w, 0, hours)).sort((a, b) => a - b);
  return answered.length === 0 ? null : answered[Math.floor(answered.length / 2)];
}

export function responseTimes(input: {
  /** Now, in seconds. */
  now: number;
  /** YWS's working hours (lib/server/working-hours.ts): the clock every wait's length is on. */
  hours: WorkingHours;
  /** Every brand in the reader's reach, configured or not. */
  brands: readonly RtBrand[];
  /** Every needs-a-reply wait of those brands over the read (90 days), open or answered. */
  waits: readonly RtWait[];
  /** The waits the model read as needing no reply, over the same read. */
  setAside: readonly RtSetAside[];
  /** The waits marked answered elsewhere, over the same read. */
  elsewhere?: readonly RtElsewhere[];
  /** The Client Inbox on a pod and brand: where "Waiting now" goes. */
  inboxHref?: (scope: { pod: string | null; brand: string | null }) => string;
  triage: RtTriage;
  reading: RtReading;
  href: (r: RtReading) => string;
  /** Did the reader of the channel list ever run? False: nothing has been read at all. */
  anyChannel: boolean;
  /** Pod labels by address, for the scope's own name in a sentence. */
  podLabels: ReadonlyMap<string, string>;
  /** The reader may open Settings → Brands, where the empty states send people. */
  settings: boolean;
}): RtModel {
  const { now, brands, reading, href } = input;
  const clock = input.hours;
  // ⚠️ EACH BRAND ON ITS OWN "NOW" (`brandNow`): an archived brand is judged at
  // its moment, by the same rules, so a message open at its archive keeps the
  // age it had then and its window ends there rather than sliding to empty.
  const bySlug = new Map(brands.map((b) => [b.slug, b]));
  const at = (slug: string): number => brandNow(bySlug.get(slug), now);
  // A wait's length is working time; a working day is the setting's (8 hours on 9–5).
  const len = (w: RtWait): number => waited(w, at(w.brand), clock);
  const wday = workingDayLength(clock);
  const fmt = (s: number): string => dur(s, wday);
  const win = reading.win;
  const go = (o: Partial<RtReading>): string => href({ ...reading, ...o });
  const picked = reading.brand === null ? null : (brands.find((b) => b.slug === reading.brand) ?? null);
  // The scope's numbers are on one clock: the page's, or the picked brand's own.
  const scopeNow = picked === null ? now : at(picked.slug);
  const from = scopeNow - win * DAY;

  const byBrand = new Map<string, RtWait[]>();
  for (const w of input.waits) {
    const list = byBrand.get(w.brand);
    if (list) list.push(w);
    else byBrand.set(w.brand, [w]);
  }
  const waitsOf = (slug: string): RtWait[] => byBrand.get(slug) ?? [];
  const inWindow = (w: RtWait): boolean => {
    const n = at(w.brand);
    return w.start >= n - win * DAY && w.start <= n;
  };
  const countOf = (slug: string): number => waitsOf(slug).filter(inWindow).length;

  // ── The scope bar: every brand in reach, a count each (the mock's), the pods summed.
  const { pods, brands: brandPills, podBrands } = slackScopePills({ brands, countOf, pod: reading.pod, brand: reading.brand, go: (o) => go(o) });

  const windows = RT_WINDOWS.map((w) => ({ key: String(w), label: `Last ${w} days`, href: go({ win: w }) }));

  const scoped = reading.brand === null ? podBrands : podBrands.filter((b) => b.slug === reading.brand);
  const readable = scoped.filter((b) => b.word === "reading");
  // ⚠️ AN ARCHIVED BRAND IS IN A POD'S NUMBERS ONLY WHEN IT IS THE SCOPE: it owes
  // nothing now, and its moment is not the pod's (the affiliate work table's
  // summary rule). Its row in the table, and its page, are as of its moment.
  const counted = picked !== null ? readable : readable.filter((b) => !b.archived);
  const shelved = readable.filter((b) => !counted.includes(b));
  // "None of the brands in No pod" read as a typo (QA): the brands nobody leads are said so.
  const scopeWords =
    reading.pod === null ? "None of the brands" : reading.pod === "none" ? "None of the brands nobody leads" : `None of the brands in ${input.podLabels.get(reading.pod) ?? "this pod"}`;

  // ── The footer: what the numbers leave out, said once.
  const notes: FooterNote[] = [];
  const dark = scoped.filter((b) => b.word === "not-set" || b.word === "bot-missing");
  if (dark.length > 0 && readable.length > 0) {
    notes.push({
      text: `${plural(dark.length, "brand")} in this scope ${dark.length === 1 ? "has" : "have"} no Slack channel the YWS bot can read, so ${dark.length === 1 ? "it isn't" : "they aren't"} counted: ${listOf(dark.map((b) => b.name))}.`,
      defect: true,
    });
  }
  const reading90 = scoped.flatMap((b) => b.stillReading.map((c) => `${c} (${b.name})`));
  if (reading90.length > 0 && readable.length > 0) {
    notes.push({ text: `Still reading the last 90 days of ${listOf(reading90)}, so ${reading90.length === 1 ? "it is" : "they are"} left out until the read is done.`, caution: true });
  }

  // Picked, its footer must not say the bot reads its channel (the clean sentence's words).
  if (picked?.archived && picked.word === "reading") {
    notes.push({ text: `${picked.name} is archived, so its Slack channels aren't read: these numbers are as of its archive date.` });
  }
  if (shelved.length > 0 && counted.length > 0) {
    const one = shelved.length === 1;
    notes.push({ text: `${listOf(shelved.map((b) => b.name))} ${one ? "is" : "are"} archived, so ${one ? "it isn't" : "they aren't"} counted here. Pick ${one ? "it" : "one"} to see its messages as of its archive date.` });
  }

  const blank = { value: DASH, sub: "", delta: null };
  const asOf = picked?.archived && picked.asOf ? slackAsOfLine(picked.asOf, now) : null;
  const base = { pods, brands: brandPills, windows, asOf, headline: blank, tiles: [], daily: [], buckets: [], bucketTotal: 0, hours: [], columns: [], rows: [], rowsEmpty: null, notes };

  // ── A picked brand with nothing to read, or a scope with none.
  if (!input.anyChannel) {
    return { ...base, empty: { title: "The YWS bot's channels haven't been read yet.", act: "Client messages show here once the Slack reader has run.", settings: false } };
  }
  if (picked !== null && picked.word !== "reading" && picked.empty !== null) {
    return { ...base, empty: { ...picked.empty, settings: input.settings && picked.word !== "still-reading" } };
  }
  if (readable.length === 0) {
    return {
      ...base,
      empty: {
        title: `${scopeWords} has a Slack channel the YWS bot can read.`,
        act: "Ask an admin to link each brand's client channel on Settings → Brands, and to add the bot to it.",
        settings: input.settings,
      },
    };
  }
  if (counted.length === 0) {
    const one = shelved.length === 1;
    return {
      ...base,
      notes: [],
      empty: {
        title: one ? "The only brand here with a Slack channel is archived." : "The only brands here with a Slack channel are archived.",
        act: `Pick ${one ? "it" : "one"} to see its messages as of its archive date.`,
        settings: false,
      },
    };
  }

  // ── The numbers.
  const scopeWaits = counted.flatMap((b) => waitsOf(b.slug));
  const cur = scopeWaits.filter(inWindow);
  const readableSlugs = new Set(counted.map((b) => b.slug));
  const here = (w: { brand: string; start: number }): boolean => readableSlugs.has(w.brand) && w.start >= from && w.start <= scopeNow;
  const asideHere = input.setAside.filter((w) => w.by === "model" && here(w)).length;
  const markedHere = input.setAside.filter((w) => w.by === "person" && here(w)).length;
  const elsewhereHere = (input.elsewhere ?? []).filter((w) => here(w) && (w.how === undefined || w.how === "elsewhere")).length;
  const reactedHere = (input.elsewhere ?? []).filter((w) => here(w) && w.how === "reaction").length;
  const settledHere = (input.elsewhere ?? []).filter((w) => here(w) && w.how === "settled").length;
  const inbox = (pod: string | null, brand: string | null): string | null => (input.inboxHref ? input.inboxHref({ pod, brand }) : null);
  const one = line(cur, scopeNow, FAST, clock);
  const four = line(cur, scopeNow, GOAL, clock);
  const prev = line(
    scopeWaits.filter((w) => w.start >= from - win * DAY && w.start < from),
    scopeNow,
    FAST,
    clock,
  );

  let delta: RtModel["headline"]["delta"] = null;
  if (one.asked > 0 && prev.asked > 0) {
    const d = pct(one.ok, one.asked) - pct(prev.ok, prev.asked);
    delta = {
      text: `${d > 0 ? "▲" : d < 0 ? "▼" : "■"} ${Math.abs(d)} pts vs the ${win} days before (${pct(prev.ok, prev.asked)}%)`,
      tone: d > 0 ? "up" : d < 0 ? "down" : "flat",
    };
  }
  const subParts = [`${one.ok.toLocaleString("en-US")} of ${plural(one.asked, "client message")} that needed a reply`];
  if (one.pending > 0) subParts.push(`${plural(one.pending, "more", "more")} still inside the hour`);
  const headline = { value: pctText(one.ok, one.asked), sub: subParts.join(" · "), delta };

  const med = typical(cur, clock);
  const open = scopeWaits.filter((w) => w.end === null && !w.archived);
  const oldest = open.length === 0 ? null : open.reduce((a, w) => (w.start < a.start ? w : a));
  // "Over a day" is over a working day: a message answered the next working morning didn't wait a day.
  const overDay = cur.filter((w) => len(w) > wday).length;
  const stillOver = cur.filter((w) => w.end === null && !w.archived && len(w) > wday).length;
  const tiles = [
    {
      key: "within-4h",
      label: "Within 4 hours",
      value: pctText(four.ok, four.asked),
      hint: four.pending > 0 ? `${four.ok} of ${four.asked}, ${four.pending} still inside 4 hours` : `${four.ok} of ${four.asked}`,
      href: null,
    },
    { key: "typical", label: "Typical first reply", value: med === null ? DASH : fmt(med), hint: med === null ? "No replies in this window" : "Half of replies came faster", href: null },
    // The Client Inbox on this scope: the same waits, one row each.
    {
      key: "waiting",
      label: "Waiting now",
      value: open.length.toLocaleString("en-US"),
      hint: oldest === null ? "Nothing waiting" : `Oldest has waited ${fmt(len(oldest))}`,
      href: open.length === 0 ? null : inbox(reading.pod, reading.brand),
    },
    // "Over a day" in the buckets below is the ANSWERED ones; this tile counts
    // the open ones too, so its hint says how many are still waiting, or the
    // two read as disagreeing an inch apart (QA).
    {
      key: "over-day",
      label: "Waited over a day",
      value: overDay.toLocaleString("en-US"),
      hint:
        cur.length === 0
          ? "No messages in this window"
          : [`${pct(overDay, cur.length)}% of messages`, stillOver > 0 ? `${stillOver.toLocaleString("en-US")} still waiting` : null].filter(Boolean).join(", "),
      href: null,
    },
  ];

  // ── By day: always 30 days, so the window shows in context; its own days in full colour.
  const today = nyDay(scopeNow);
  const days: string[] = [];
  for (let i = 29; i >= 0; i--) days.push(addDaysUtc(today, -i));
  const winDays = new Set(days.slice(-win));
  const perDay = new Map<string, RtWait[]>(days.map((d) => [d, []]));
  for (const w of scopeWaits) perDay.get(nyDay(w.start))?.push(w);
  const daily: RtColumn[] = days.map((d) => {
    const ws = perDay.get(d) ?? [];
    const l = line(ws, scopeNow, FAST, clock);
    return { key: d, label: formatShort(d), v: l.asked === 0 ? null : l.ok / l.asked, muted: !winDays.has(d), title: columnTitle(formatShort(d), ws.length, l) };
  });

  // ── How long they waited: the window's waits, one bucket each.
  const answered = (lo: number, hi: number) => cur.filter((w) => w.end !== null && len(w) > lo && len(w) <= hi).length;
  // A day is a working day (8 hours on 9–5), as the "Waited over a day" tile's.
  const dayEnd = Math.max(GOAL, wday);
  const bucketDefs: Array<[string, number, boolean]> = [
    ["Under 15 min", answered(-1, 15 * 60), false],
    ["15–60 min", answered(15 * 60, HOUR), false],
    ["1–4 hours", answered(HOUR, GOAL), false],
    [`4–${Math.round(dayEnd / HOUR)} hours`, answered(GOAL, dayEnd), false],
    ["Over a day", answered(dayEnd, Number.POSITIVE_INFINITY), false],
    ["No reply yet", cur.filter((w) => w.end === null).length, true],
  ];
  const buckets = bucketDefs.map(([bucket, value, bad]) => ({ bucket, value, share: cur.length === 0 ? null : value / cur.length, bad }));

  // ── By the hour the client wrote.
  const perHour: RtWait[][] = Array.from({ length: 24 }, () => []);
  for (const w of cur) perHour[nyHour(w.start)].push(w);
  const hourLabel = (h: number): string => (h === 0 ? "12a" : h < 12 ? `${h}a` : h === 12 ? "12p" : `${h - 12}p`);
  const hours: RtColumn[] = perHour.map((ws, h) => {
    const l = line(ws, scopeNow, FAST, clock);
    return { key: String(h), label: hourLabel(h), v: l.asked === 0 ? null : l.ok / l.asked, muted: l.asked < SHAKY, title: columnTitle(`${hourLabel(h)} New York`, ws.length, l) };
  });

  // ── By brand: the pod's brands with anything to show, the picked one lit.
  // A brand with nothing read has no waits, so the last filter leaves it out.
  const rows: RtRow[] = podBrands
    .map((b) => {
      const all = waitsOf(b.slug);
      const ws = all.filter(inWindow);
      const l1 = line(ws, at(b.slug), FAST, clock);
      const l4 = line(ws, at(b.slug), GOAL, clock);
      const m = typical(ws, clock);
      const waiting = all.filter((w) => w.end === null && !w.archived).length;
      return {
        slug: b.slug,
        name: b.archived ? `${b.name} (archived)` : b.name,
        asOf: b.archived && b.asOf ? slackAsOfLine(b.asOf, now) : null,
        // Clicking the lit brand lets it go; any other picks it, in its own pod.
        href: reading.brand === b.slug ? go({ brand: null }) : go({ pod: b.pod, brand: b.slug }),
        on: reading.brand === b.slug,
        n: ws.length,
        p1: l1.asked === 0 ? null : l1.ok / l1.asked,
        p1Text: pctText(l1.ok, l1.asked),
        p4Text: pctText(l4.ok, l4.asked),
        medText: m === null ? DASH : fmt(m),
        med: m,
        p4: l4.asked === 0 ? null : l4.ok / l4.asked,
        waiting,
        waitingText: waiting === 0 ? DASH : waiting.toLocaleString("en-US"),
        waitingHref: waiting === 0 ? null : inbox(b.pod, b.slug),
        pod: b.pod === "none" ? "No pod" : b.podLabel,
        lead: b.repliesMost ?? DASH,
      };
    })
    .filter((r) => r.n > 0 || r.waiting > 0);
  const key = (r: (typeof rows)[number]): number | string | null =>
    reading.sort === "name" ? r.name.toLowerCase() : reading.sort === "pod" ? r.pod : reading.sort === "lead" ? r.lead : reading.sort === "n" ? r.n : reading.sort === "waiting" ? r.waiting : r[reading.sort];
  rows.sort((a, b) => {
    // An empty cell sorts as the lowest value, as the mock's `?? -1`.
    const x = key(a);
    const y = key(b);
    const c = x === y ? 0 : x === null ? -1 : y === null ? 1 : x < y ? -1 : 1;
    return c * reading.dir || b.n - a.n || a.name.localeCompare(b.name);
  });
  const columns = (
    [
      ["name", "Brand", false],
      ["n", "Messages", true],
      ["p1", "Within 1 hour", true],
      ["p4", "Within 4 hours", true],
      ["med", "Typical reply", true],
      ["waiting", "Waiting now", true],
      ["pod", "Pod", false],
      ["lead", "Replies most", false],
    ] as Array<[RtSort, string, boolean]>
  ).map(([k, label, right]) => {
    const on = reading.sort === k;
    return {
      key: k,
      label,
      right,
      href: go({ sort: k, dir: on ? (reading.dir === 1 ? -1 : 1) : FIRST_DIR[k] }),
      arrow: on ? (reading.dir === 1 ? "↑" : "↓") : "",
    };
  });

  return {
    ...base,
    notes: [...notes, ...clockNotes(clock), ...decidedNotes(cur, asideHere, input.triage), ...markedNotes(markedHere, elsewhereHere, reactedHere, settledHere, cur.filter((w) => w.archived).length)],
    empty: null,
    headline,
    tiles,
    daily,
    buckets,
    bucketTotal: cur.length,
    hours,
    columns,
    rows,
    rowsEmpty: rows.length === 0 ? `No client message in the last ${win} days needed a reply, and nothing is waiting.` : null,
  };
}

/**
 * The clock the numbers are on, said once under them (X7), when it isn't the
 * wall's: every hour of every day is what anyone would assume, and needs no line.
 */
export function clockNotes(hours: WorkingHours): FooterNote[] {
  return isAllHours(hours) ? [] : [{ text: `Reply times count working hours only: ${workingHoursText(hours)}. An admin sets them on Settings → Feature flags.` }];
}

const dollars = (usd: number): string => (Number.isInteger(usd) ? `$${usd}` : `$${usd.toFixed(2)}`);

/**
 * Who decided which messages count, said under the numbers it qualifies
 * and never under an empty state: what the model set aside, and how many of
 * the counted ones the rules decided, with the reason when it is the ceiling.
 */
function decidedNotes(cur: readonly RtWait[], aside: number, triage: RtTriage): FooterNote[] {
  const notes: FooterNote[] = [];
  const byRules = cur.filter((w) => w.by === "rules").length;
  if (aside > 0 || cur.some((w) => w.by === "model")) {
    notes.push({
      text:
        aside > 0
          ? `The model reads each client message for whether it needs a reply: ${plural(aside, "message")} it read as needing none (a thanks, an FYI) ${aside === 1 ? "isn't" : "aren't"} counted.`
          : "The model reads each client message for whether it needs a reply.",
    });
  }
  const these = byRules === 0 ? "" : byRules === cur.length ? ` (all ${plural(cur.length, "message")} counted here)` : ` (${byRules.toLocaleString("en-US")} of the ${plural(cur.length, "message")} counted here)`;
  if (triage.exhausted && triage.limitUsd === 0) {
    notes.push({ text: `The model is off (its daily budget is set to $0), so simple rules decide which client messages need a reply${these}.`, caution: true });
  } else if (triage.exhausted) {
    notes.push({ text: `The model's ${dollars(triage.limitUsd)} daily budget is spent, so until midnight UTC it reads nothing more and simple rules decide every client message it hasn't read${these}.`, caution: true });
  } else if (byRules > 0) {
    notes.push({
      text:
        byRules === cur.length
          ? "The model hasn't read these client messages yet, so simple rules decide which need a reply: a short thanks or okay doesn't count, but a few that need nothing still do."
          : `The model hasn't read ${byRules.toLocaleString("en-US")} of the ${plural(cur.length, "client message")} counted here yet, so simple rules decided ${byRules === 1 ? "it" : "those"}: a short thanks or okay doesn't count, but a few that need nothing still do.`,
      caution: true,
    });
  }
  return notes;
}

/**
 * What people marked in the Client Inbox, said under the numbers: the messages
 * someone said need no reply, and the ones answered somewhere this page can't
 * see (a DM, a call), which can't be timed.
 */
function markedNotes(noReply: number, elsewhere: number, reacted = 0, settled = 0, archived = 0): FooterNote[] {
  const notes: FooterNote[] = [];
  if (noReply > 0) {
    notes.push({ text: `${plural(noReply, "client message")} someone at YWS marked as needing no reply ${noReply === 1 ? "isn't" : "aren't"} counted.` });
  }
  if (elsewhere > 0) {
    notes.push({ text: `${plural(elsewhere, "client message")} marked answered elsewhere (in a DM or on a call) ${elsewhere === 1 ? "isn't" : "aren't"} counted: when the answer came isn't known.` });
  }
  if (reacted > 0) {
    notes.push({
      text: `${plural(reacted, "client message")} that only needed a quick acknowledgement ${reacted === 1 ? "was" : "were"} acknowledged with a reaction, and ${reacted === 1 ? "isn't" : "aren't"} counted: a reaction isn't a reply, and Slack doesn't say when it came.`,
    });
  }
  if (settled > 0) {
    notes.push({
      text: `${plural(settled, "client message")} the model read as settled without a reply in Slack (handled on a call or in a DM, replaced by the client's next message, or answered by the client) ${settled === 1 ? "isn't" : "aren't"} counted: nothing was owed after that, and there is no reply to time.`,
    });
  }
  if (archived > 0) {
    notes.push({ text: `${plural(archived, "client message")} someone at YWS archived without a reply ${archived === 1 ? "counts" : "count"} as not answered, and ${archived === 1 ? "isn't" : "aren't"} in Waiting now.` });
  }
  return notes;
}

/** A column's hover: its share, its counts, and what is still inside the hour. */
function columnTitle(label: string, n: number, l: { ok: number; asked: number; pending: number }): string {
  if (n === 0) return `${label} · No client messages`;
  const parts = [label];
  if (l.asked > 0) parts.push(`${pct(l.ok, l.asked)}% within 1 hour`, `${l.ok} of ${plural(l.asked, "message")}`);
  if (l.pending > 0) parts.push(`${plural(l.pending, "message")} still inside the hour`);
  return parts.join(" · ");
}

/** The page's address for a reading: only what differs from the default is in it. */
export function responseTimesQuery(r: RtReading): Array<[string, string]> {
  const pairs: Array<[string, string]> = [];
  if (r.pod !== null) pairs.push(["pod", r.pod]);
  if (r.brand !== null) pairs.push(["brand", r.brand]);
  if (r.win !== DEFAULT_RT_WIN) pairs.push(["win", String(r.win)]);
  if (r.sort !== "p1" || r.dir !== FIRST_DIR.p1) {
    pairs.push(["sort", r.sort]);
    pairs.push(["dir", r.dir === 1 ? "asc" : "desc"]);
  }
  return pairs;
}
